import React, { useState } from 'react';
import { Container, Card, CardBody, CardHeader, Row, Col, Badge, Button } from 'reactstrap';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CommentIcon from '@mui/icons-material/Comment';
import { useHistory, useLocation } from 'react-router-dom';
import { showNotification, successNofication } from 'src/shared/components/Notification';
import ConfirmationDialog from 'src/shared/components/ConfirmationDialog';
import { APPROVAL_STATUS_APPROVED, OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED } from 'src/utils/pnd/PndConstants';
import PndRestApi from 'src/utils/pnd/PndRestApi';

const OrderDetailsPage = () => {
    const history = useHistory();
    const location = useLocation();
    const order = location.state?.order;
    const [isWaitTimeExceeded, setIsWaitTimeExceeded] = useState(false);
    const [showApproveConfirmation, setShowApproveConfirmation] = useState(false);

    React.useEffect(() => {
        if (!order) return;
        const checkWaitTime = () => {
            const now = new Date();
            const start = new Date(order.goodsReceivedTime);
            const diffInSeconds = Math.floor((now - start) / 1000);
            setIsWaitTimeExceeded(diffInSeconds > 60);
        };

        checkWaitTime();
        const interval = setInterval(checkWaitTime, 1000);
        return () => clearInterval(interval);

    }, [order]);

    const handleDecline = () => {
        history.push({
            pathname: '/web_app/order/decline',
            state: { order: order, fromHistory: fromHistory }
        });
    };

    const handleApprove = () => {
        setShowApproveConfirmation(true);
    };

    const confirmApprove = () => {
        // Logic to approve order
        console.log("Approving order:", order);
        console.log("Full order object:", JSON.stringify(order));

        // Fallback: use order.orderId, or order.id if orderId is undefined
        const orderId = order.orderId ?? order.id;
        console.log("Resolved orderId:", orderId);

        let Obj = {
            "approvalStatus": APPROVAL_STATUS_APPROVED,
            "orderId": orderId,
        };

        PndRestApi.securityApprovalProcess(Obj).then(response => {
            if (response && response.data && response.data.status === 200) {
                successNofication(
                    "theme-light",
                    "ltr",
                    "SuccessCode: 200",
                    `Order ${order.orderNo} Approved Successfully`
                );

                setShowApproveConfirmation(false);
                history.goBack();
            } else {
                showNotification(
                    "theme-light",
                    "ltr",
                    "ErrorCode: 100",
                    response?.data?.message || `Order ${order.orderNo} Approved Failed`
                );
            }

        });
    };


    const fromHistory = location.state?.fromHistory;
    const backRoute = fromHistory ? '/web_app/security/history' : '/web_app/order/security_approval';

    if (!order) return (
        <Container className="p-3 text-center">
            <h5 className="text-muted">No order details available. Please select an order from the list.</h5>
            <Button color="primary" onClick={() => history.push(backRoute)}>
                Back to {fromHistory ? 'History' : 'Order List'}
            </Button>
        </Container>
    );

    return (
        <Container className="p-0 pt-3" fluid>
            <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px', background: '#f8f9fa' }}>
                <CardHeader className="bg-white border-0 py-3" style={{ borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }}>
                    <div className="d-flex align-items-center">
                        <Button color="link" onClick={() => history.push(backRoute)} className="text-dark p-0" style={{ textDecoration: 'none', position: 'absolute', left: '20px', zIndex: 1 }}>
                            <div className="d-flex align-items-center gap-1">
                                <ArrowBackIcon fontSize="small" />
                                <span className="fw-bold" style={{ fontSize: '0.9rem' }}>Back</span>
                            </div>
                        </Button>
                        <h5 className="mb-0 fw-bold text-dark w-100 text-center">Order Details</h5>
                        <div style={{ position: 'absolute', right: '20px', zIndex: 1 }}>
                            <Badge bg={order.orderStatus === "PGR" ? "success" : "warning"} className="px-3 py-2 rounded-pill">
                                {order.orderStatus}
                            </Badge>
                        </div>
                    </div>
                </CardHeader>
                <CardBody className="p-3 p-md-4">
                    <div className="mb-4 text-center">
                        <h3 className="fw-bold text-primary mb-1">DLV NO:{order.orderNo}</h3>
                        {
                            order.erpNo && (
                                <p className="text-muted small">ERP Order No: {order.erpNo}</p>
                            )}
                    </div>

                    <Row className="mb-4 g-3">
                        <Col xs={4}>
                            <div className="p-3 bg-white rounded shadow-sm border-start border-4 border-warning h-100">
                                <span className="text-muted d-block mb-1 fw-semibold" style={{ fontSize: '0.82rem' }}>Order Status</span>
                                <h5 className="fw-bold mb-0 text-break">{order.orderStatus}</h5>
                            </div>
                        </Col>
                        {order?.orderStatusId === OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED &&
                            <Col xs={4}>
                                <div className="p-3 bg-white rounded shadow-sm border-start border-4 border-warning h-100">
                                    <span className="text-muted d-block mb-1 fw-semibold" style={{ fontSize: '0.82rem' }}>Rider</span>
                                    <h5 className="fw-bold mb-0 text-break">{order.riderName}</h5>
                                </div>
                            </Col>
                        }
                        <Col xs={4}>
                            <div className="p-3 bg-white rounded shadow-sm border-start border-4 border-info h-100">
                                <span className="text-muted d-block mb-1 fw-semibold" style={{ fontSize: '0.82rem' }}>Customer</span>
                                <h5 className="fw-bold mb-0 text-break">{order.customerName} - {order.customerCode}</h5>
                            </div>
                        </Col>
                    </Row>

                    {/* Dates Section */}
                    <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
                        <div className="row g-2">
                            <div className="col-12">
                                <div className="d-flex align-items-center gap-2 p-2 rounded-2" style={{ background: '#f0f4ff' }}>
                                    <AccessTimeIcon style={{ color: '#667eea', fontSize: '1.2rem' }} />
                                    <div className="flex-grow-1 d-flex justify-content-between align-items-center">
                                        <span className="fw-semibold text-muted" style={{ fontSize: '0.83rem' }}>Goods Received Date &amp; Time</span>
                                        <span className="fw-bold" style={{ fontSize: '0.92rem' }}>{order?.goodsReceivedDateTime ? new Date(order.goodsReceivedDateTime).toLocaleString() : 'N/A'}</span>
                                    </div>
                                </div>
                            </div>
                            {order?.orderStatusId === OPERATION_TYPE_FOR_DROP_OFF_ASSIGNED && (
                                <div className="col-12">
                                    <div className="d-flex align-items-center gap-2 p-2 rounded-2" style={{ background: '#f0fff8' }}>
                                        <EventAvailableIcon style={{ color: '#20c997', fontSize: '1.2rem' }} />
                                        <div className="flex-grow-1 d-flex justify-content-between align-items-center">
                                            <span className="fw-semibold text-muted" style={{ fontSize: '0.83rem' }}>Order Assigned Date &amp; Time</span>
                                            <span className="fw-bold" style={{ fontSize: '0.92rem' }}>{order?.assignedDateTime ? new Date(order.assignedDateTime).toLocaleString() : 'N/A'}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Approval Status */}
                    <div className="bg-white rounded-3 shadow-sm p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="fw-semibold text-muted" style={{ fontSize: '0.85rem' }}>Approval Status</span>
                            {order.securityApprovalStatus == null
                                ? <span style={{ background: '#fff3cd', color: '#856404', padding: '4px 14px', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 700 }}>⏳ Pending</span>
                                : order.securityApprovalStatus == 1
                                    ? <span style={{ background: '#d4edda', color: '#155724', padding: '4px 14px', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 700 }}>✅ Approved</span>
                                    : <span style={{ background: '#f8d7da', color: '#721c24', padding: '4px 14px', borderRadius: '20px', fontSize: '0.82rem', fontWeight: 700 }}>❌ Declined</span>
                            }
                        </div>
                    </div>

                    {/* Decline Details */}
                    {order.declinedDetails?.length > 0 &&
                        <div className="rounded-3 shadow-sm p-3 mb-4" style={{ background: '#fff5f5', border: '1px solid #ffd6d6' }}>
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <WarningAmberIcon style={{ color: '#dc3545', fontSize: '1.1rem' }} />
                                <span className="fw-bold text-danger" style={{ fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Decline Information</span>
                                <span className="fw-bold badge ms-auto" style={{ background: '#dc3545', color: 'white', fontSize: '0.85rem', padding: '4px 10px', borderRadius: '12px' }}>
                                    {order.declinedDetails.length} {order.declinedDetails.length === 1 ? 'Decline' : 'Declines'}
                                </span>
                            </div>

                            {order.declinedDetails.map((detail, idx) => (
                                <div key={idx} className="mb-3 pb-3" style={{ borderBottom: idx < order.declinedDetails.length - 1 ? '1px dashed #ffd6d6' : 'none' }}>
                                    {order.declinedDetails.length > 1 && (
                                        <div className="mb-2">
                                            <span style={{ background: '#dc3545', color: 'white', fontSize: '0.72rem', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>#{idx + 1}</span>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                        <div className="d-flex align-items-center gap-1">
                                            <PersonIcon style={{ color: '#888', fontSize: '1rem' }} />
                                            <span className="text-muted fw-semibold" style={{ fontSize: '0.83rem' }}>Declined By</span>
                                        </div>
                                        <span className="fw-bold" style={{ fontSize: '0.9rem' }}>{detail.declinedBy || '—'}</span>
                                    </div>
                                    <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                        <div className="d-flex align-items-center gap-1">
                                            <WarningAmberIcon style={{ color: '#888', fontSize: '1rem' }} />
                                            <span className="text-muted fw-semibold" style={{ fontSize: '0.83rem' }}>Declined Reason</span>
                                        </div>
                                        <span className="fw-bold text-danger" style={{ fontSize: '0.9rem', maxWidth: '60%', textAlign: 'right' }}>{detail.declinedReason || '—'}</span>
                                    </div>
                                    {detail.declinedDateTime && (
                                        <div className="d-flex justify-content-between align-items-center py-2 border-bottom">
                                            <div className="d-flex align-items-center gap-1">
                                                <AccessTimeIcon style={{ color: '#888', fontSize: '1rem' }} />
                                                <span className="text-muted fw-semibold" style={{ fontSize: '0.83rem' }}>Declined At</span>
                                            </div>
                                            <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{new Date(detail.declinedDateTime).toLocaleString()}</span>
                                        </div>
                                    )}
                                    <div className="pt-2">
                                        <div className="d-flex align-items-center gap-1 mb-1">
                                            <CommentIcon style={{ color: '#888', fontSize: '1rem' }} />
                                            <span className="text-muted fw-semibold" style={{ fontSize: '0.83rem' }}>Remarks</span>
                                        </div>
                                        <p className="mb-0 fst-italic ps-3" style={{ fontSize: '0.9rem', color: '#555' }}>{detail.remarks || 'No remarks available'}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    }
                    {!fromHistory &&
                        <div className="d-flex gap-2 justify-content-center">
                            <Button
                                color="success"
                                size="sm"
                                className="fw-bold text-white shadow-sm d-flex align-items-center justify-content-center gap-1"
                                onClick={handleApprove}
                                style={{ background: 'linear-gradient(45deg, #28a745, #20c997)', border: 'none', width: '130px' }}
                            >
                                <CheckCircleIcon style={{ fontSize: '1rem' }} /> Approve
                            </Button>
                            <Button
                                color="danger"
                                size="sm"
                                className="fw-bold text-white shadow-sm d-flex align-items-center justify-content-center gap-1"
                                onClick={handleDecline}
                                style={{ background: 'linear-gradient(45deg, #dc3545, #f86d75)', border: 'none', width: '130px' }}
                            >
                                <CancelIcon fontSize="small" /> Decline
                            </Button>
                        </div>
                    }
                </CardBody>
            </Card>

            {showApproveConfirmation && (
                <ConfirmationDialog
                    open={showApproveConfirmation}
                    onClose={() => setShowApproveConfirmation(false)}
                    onConfirm={confirmApprove}
                    title="Confirm Approval"
                    message={`Are you sure you want to approve order ${order?.orderNo}?`}
                />
            )}
        </Container>
    );
};

export default OrderDetailsPage;
